/**
 * 📨 Postacı — FCM Servisi (Fire-and-Forget v3)
 * Firebase Cloud Messaging izin, token ve dinleme işlemleri.
 * firebase.ts'teki singleton messaging instance'ını kullanır.
 *
 * Capacitor Native Push Notification ve Vite PWA destekli.
 *
 * ─── Mimari Not ───
 * requestNotificationPermission() Promise<boolean> döner:
 * - true  → Kullanıcı izin verdi, token arka planda alınıyor
 * - false → İzin reddedildi veya desteklenmiyor
 *
 * Token edinimi ve DB kayıt süreci (_acquireAndSaveTokenInBackground)
 * UI'ı bloklamadan arka planda fire-and-forget olarak çalışır.
 * Bu sayede iOS Safari'deki 8-10sn UI kilidi tamamen ortadan kalkar.
 *
 * ─── v3 Değişikliği: Web Push API Fallback ───
 * Firebase SDK bazı iOS PWA ortamlarında isSupported()=false dönebiliyor.
 * Bu durumda doğrudan Web Push API (pushManager.subscribe) ile token alınır.
 * postaci-engine Edge Function her iki token tipini de destekler.
 */
import { getToken, onMessage, deleteToken } from 'firebase/messaging'
import { withTransientRetry } from '@/lib/retryTransient'
import { getMessagingInstance } from '@/lib/firebase'
import { supabase } from '@/lib/supabase'
import { Capacitor } from '@capacitor/core'
import { FirebaseMessaging } from '@capacitor-firebase/messaging'
import { captureError } from '@/lib/sentry'
import { logWarn } from '@/lib/log'
// Bu ikisi `import.meta.env` okuyor; ayrı modülde durmasalardı
// fcmService test ortamında import EDİLEMEZDİ. Bkz. lib/pushConfig.ts
import { VAPID_KEY, WEBPUSH_VAPID_KEY } from '@/lib/pushConfig'

const MAX_RETRIES = 2
const RETRY_DELAY_MS = 2000

/** Cihazın son aldığı FCM token'ını sakladığımız local storage key'i */
export const LAST_TOKEN_KEY = 'gv_fcm_last_token'

/** Belirli süre bekleme yardımcısı */
const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms))

/** Base64 URL-safe string'i Uint8Array'e çevirir (Web Push subscribe için) */
function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4)
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/')
  const rawData = atob(base64)
  return Uint8Array.from([...rawData].map(c => c.charCodeAt(0)))
}

/** Promise'e zaman sınırı koyar — iOS'ta sonsuz beklemeyi engeller */
function withTimeout<T>(promise: Promise<T>, ms: number, label: string): Promise<T> {
  return Promise.race([
    promise,
    new Promise<never>((_, reject) =>
      setTimeout(() => reject(new Error(`⏱️ ${label} zaman aşımına uğradı (${ms / 1000}sn)`)), ms)
    ),
  ])
}

// ============================================
// SW PRE-WARMING (Modül yüklenince 1 kez tetiklenir)
// ============================================

/**
 * Service Worker registration'ını önceden ısıtır.
 * Kullanıcı henüz "Allow" demeden SW aktif hale gelmiş olabilir.
 * Background token alma sırasında bu cache kullanılır.
 */
let _preWarmedSW: Promise<ServiceWorkerRegistration | null> | null = null

function _initPreWarm(): void {
  if (Capacitor.isNativePlatform()) return
  if (!('serviceWorker' in navigator)) return

  _preWarmedSW = getActiveServiceWorkerRegistration().catch(() => null)
  _preWarmedSW.then(reg => {
    if (reg) {
    } else {
    }
  })
}

// Modül ilk import edildiğinde, ortam uygunsa pre-warm başlat
try { _initPreWarm() } catch { /* sessiz */ }

// ============================================
// SERVICE WORKER POLLING
// ============================================

/**
 * Active bir Service Worker registration al.
 * iOS PWA'da `navigator.serviceWorker.ready` hiç resolve olmayabiliyor
 * ve `statechange` event'i kaçırılabiliyor.
 * Bu nedenle POLLING kullanıyoruz — 500ms aralıklarla `reg.active` kontrol.
 */
async function getActiveServiceWorkerRegistration(): Promise<ServiceWorkerRegistration | null> {
  const MAX_POLLS = 20 // 20 × 500ms = 10 saniye max
  
  for (let i = 0; i < MAX_POLLS; i++) {
    const reg = await navigator.serviceWorker.getRegistration()
    
    if (reg?.active) {
      return reg
    }

    if (i === 0 && reg) {
    }

    if (i < MAX_POLLS - 1) {
      await delay(500)
    }
  }

  const warnMsg = '⚠️ [FCM] SW 10sn içinde active olmadı'
  logWarn(warnMsg)
  // captureWarning(warnMsg) kaldırıldı çünkü anonim/desteklemeyen tarayıcılarda gereksiz alert üretiyor.
  return null
}

// ============================================
// BACKGROUND TOKEN ACQUISITION (Fire-and-Forget)
// ============================================

/**
 * Ağır token edinimi ve DB kayıt sürecini arka planda yürütür.
 * Bu fonksiyon fire-and-forget olarak çağrılır.
 *
 * Adımlar:
 * 1. Pre-warmed SW cache'ini kontrol et, yoksa yeniden polling yap
 * 2. Firebase getToken() ile VAPID handshake
 * 3. Başarısız olursa → Web Push API ile fallback
 * 4. saveDeviceToken() ile Supabase'e kaydet
 *
 * @param onComplete — Token kaydı sonrası çağrılan callback (true=başarılı)
 */
async function _acquireAndSaveTokenInBackground(
  userId: string,
  onComplete?: (success: boolean) => void
): Promise<void> {
  try {
    const startTime = Date.now()

    // ─── Yol 1: Firebase SDK ile dene ───
    const messaging = await getMessagingInstance()
    if (messaging) {
      let lastError: Error | null = null

      for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
        try {
          if (attempt > 0) {
            await delay(RETRY_DELAY_MS)
          }

          let swRegistration: ServiceWorkerRegistration | null = null
          if (_preWarmedSW) {
            swRegistration = await _preWarmedSW
            if (!swRegistration?.active) {
              swRegistration = await getActiveServiceWorkerRegistration()
            }
          } else {
            swRegistration = await getActiveServiceWorkerRegistration()
          }

          let currentToken: string | null = null

          if (swRegistration) {
            currentToken = await withTimeout(
              getToken(messaging, {
                vapidKey: VAPID_KEY,
                serviceWorkerRegistration: swRegistration,
              }),
              10000,
              'FCM Token alma (BG)'
            )
          } else {
            currentToken = await withTimeout(
              getToken(messaging, { vapidKey: VAPID_KEY }),
              10000,
              'FCM Token alma (BG, SW olmadan)'
            )
          }

          if (!currentToken) {
            throw new Error('FCM boş token döndürdü.')
          }

          await saveDeviceToken(userId, currentToken)
          const elapsed = Date.now() - startTime
          onComplete?.(true)
          return
        } catch (err) {
          lastError = err instanceof Error ? err : new Error(String(err))
          logWarn(`⚠️ [FCM-BG] Firebase deneme ${attempt + 1} başarısız:`, lastError.message)
        }
      }

      const failMsg = '⚠️ [FCM-BG] Firebase SDK tüm denemelerde başarısız'
      if (lastError) captureError(lastError, { service: 'fcmService', action: 'firebaseSdkFallback', etiket: failMsg })
    } else {
      logWarn('⚠️ [FCM-BG] Firebase Messaging başlatılamadı — Web Push fallback deneniyor...')
    }

    // ─── Yol 2: Web Push API Fallback (iOS PWA için kritik) ───
    await _acquireTokenViaWebPush(userId)
    const elapsed = Date.now() - startTime
    onComplete?.(true)
  } catch (outerError) {
    captureError(outerError, { service: 'fcmService', action: '_acquireAndSaveTokenInBackground' })
    onComplete?.(false)
  }
}

/**
 * Web Push API ile doğrudan token alan fallback fonksiyonu.
 * Firebase SDK'sı desteklenmediğinde veya başarısız olduğunda kullanılır.
 * Özellikle iOS 16.4+ PWA için kritik.
 */
async function _acquireTokenViaWebPush(userId: string): Promise<void> {
  if (!('PushManager' in window)) {
    throw new Error('PushManager bu tarayıcıda desteklenmiyor')
  }

  const vapidKey = WEBPUSH_VAPID_KEY || VAPID_KEY
  if (!vapidKey) {
    throw new Error('Web Push VAPID key tanımlı değil')
  }

  const swRegistration = await getActiveServiceWorkerRegistration()
  if (!swRegistration) {
    throw new Error('Active Service Worker bulunamadı')
  }

  // Mevcut subscription varsa onu kullan
  let subscription = await swRegistration.pushManager.getSubscription()

  if (!subscription) {
    subscription = await withTimeout(
      swRegistration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidKey).buffer as ArrayBuffer,
      }),
      10000,
      'Web Push Subscribe'
    )
  }

  if (!subscription) {
    throw new Error('Web Push subscription oluşturulamadı')
  }

  // Subscription JSON'ını token olarak kaydet
  // postaci-engine bu formatı tanıyıp Web Push ile gönderim yapacak
  const subscriptionJson = JSON.stringify(subscription.toJSON())

  await saveDeviceToken(userId, subscriptionJson)
}

// ============================================
// PUBLIC API
// ============================================

/**
 * Kullanıcıdan bildirim izni iste.
 *
 * ─── Fire-and-Forget Mimari ───
 * Bu fonksiyon SADECE izin sonucunu döner (true/false).
 * Token edinimi ve DB kayıt süreci arka planda sessizce çalışır.
 * Bu sayede UI anında serbest kalır.
 *
 * @param userId — Token'ın kaydedileceği kullanıcı ID'si
 * @param onProgress — Her adımda UI'a bilgi veren callback
 * @returns true (izin verildi) | false (reddedildi/desteklenmiyor)
 */
export async function requestNotificationPermission(
  userId: string,
  onProgress?: (step: string) => void,
  onTokenSaved?: (success: boolean) => void
): Promise<boolean> {
  const report = (msg: string) => {
    onProgress?.(msg)
  }

  // ─── Capacitor Native (iOS/Android) Flow ───
  if (Capacitor.isNativePlatform()) {
    try {
      report('Capacitor izin kontrol ediliyor...')
      let permStatus = await FirebaseMessaging.checkPermissions()
      if (permStatus.receive === 'prompt') {
        permStatus = await FirebaseMessaging.requestPermissions()
      }

      if (permStatus.receive !== 'granted') {
        logWarn(`⚠️ [Capacitor Push] Bildirim izni verilmedi:`, permStatus)
        return false
      }

      _acquireCapacitorTokenInBackground(userId, onTokenSaved)
      return true
    } catch (e: any) {
      captureError(e, { service: 'fcmService', action: 'requestNativePermission' })
      return false
    }
  }

  // ─── Web (PWA) Flow ───
  if (!('Notification' in window)) {
    report('❌ Notification API bulunamadı')
    return false
  }

  if (!('serviceWorker' in navigator)) {
    report('❌ Service Worker desteği bulunamadı')
    return false
  }

  // ⚠️ v3: Firebase Messaging kontrolünü kaldırıyoruz.
  // iOS PWA'da isSupported() false dönebiliyor.
  // Web Push API fallback sayesinde bu artık bloklamayacak.

  try {
    report('İzin isteniyor...')
    const permission = await Notification.requestPermission()

    if (permission !== 'granted') {
      logWarn('⚠️ [FCM] Bildirim izni verilmedi:', permission)
      return false
    }

    report('✅ İzin verildi! Token arka planda alınıyor...')

    // 🔥 Fire-and-Forget with callback:
    // Token edinimini arka planda başlat, sonuç callback ile bildir
    _acquireAndSaveTokenInBackground(userId, onTokenSaved)

    return true
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : String(error)
    report(`❌ Hata: ${errorMessage}`)
    captureError(error, { service: 'fcmService', action: 'requestWebPermission' })
    return false
  }
}

// ============================================
// CAPACITOR BACKGROUND TOKEN (Native)
// ============================================

/**
 * Native platformlarda token'ı listener ile arka planda al ve kaydet.
 */
function _acquireCapacitorTokenInBackground(
  userId: string,
  onTokenSaved?: (saved: boolean) => void
): void {
  try {
    let callbackFired = false;
    let fallbackTimeout: NodeJS.Timeout | null = null;

    // 1. Refresh ihtimaline ve asenkron yarış durumlarına karşı dinleyiciye HEMEN kaydol
    FirebaseMessaging.addListener('tokenReceived', async (event) => {
      if (fallbackTimeout) clearTimeout(fallbackTimeout);
      try {
        await saveDeviceToken(userId, event.token)
        if (onTokenSaved && !callbackFired) {
          callbackFired = true
          onTokenSaved(true)
        }
      } catch (err) {
        captureError(err, { service: 'fcmService', action: 'tokenReceivedListener' })
      }
    })

    // 2. Bekleme yapmadan anında token iste. APNs hazırsa anında döner, değilse fail olur ve listener beklenir.
    FirebaseMessaging.getToken().then(async ({ token }) => {
      if (fallbackTimeout) clearTimeout(fallbackTimeout);
      try {
        await saveDeviceToken(userId, token)
        if (onTokenSaved && !callbackFired) {
          callbackFired = true
          onTokenSaved(true)
        }
      } catch (err) {
        captureError(err, { service: 'fcmService', action: 'getTokenManualCheck' })
        if (onTokenSaved && !callbackFired) {
          callbackFired = true
          onTokenSaved(false)
        }
      }
    }).catch(err => {
      logWarn(`⚠️ [Capacitor Push BG] Token anında alınamadı, listener bekleniyor:`, err)
      
      // 3. Fallback Gateway: 10 saniye bekle. Gelmezse diagnostic alert ile pes et.
      fallbackTimeout = setTimeout(() => {
        if (!callbackFired) {
          logWarn(`⚠️ [Capacitor Push BG] 10s timeout: Token alınamadı (No APNS token specified)`)
          callbackFired = true
          if (onTokenSaved) onTokenSaved(false)
        }
      }, 10000) // iOS APNs token delay tolerance (Event Gate)
    })

  } catch (e: any) {
    captureError(e, { service: 'fcmService', action: 'backgroundTokenAcquisition' })
    if (onTokenSaved) onTokenSaved(false)
  }
}

// ============================================
// TOKEN PERSISTENCE
// ============================================

/**
 * FCM token'ı Supabase user_devices tablosuna kaydet/güncelle.
 */
export async function saveDeviceToken(userId: string, token: string): Promise<void> {
  if (!userId || !token) return

  let platform = 'web'
  if (Capacitor.isNativePlatform()) {
    const capPlatform = Capacitor.getPlatform()
    platform = capPlatform === 'ios' ? 'ios' : capPlatform === 'android' ? 'android' : 'web'
  } else {
    const ua = navigator.userAgent
    const isIOS =
      /iPhone|iPad|iPod/.test(ua) ||
      (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
    const isAndroid = /Android/.test(ua)
    platform = isIOS ? 'ios' : isAndroid ? 'android' : 'web'
  }

  localStorage.setItem(LAST_TOKEN_KEY, token)

  // Yazma `onConflict: 'fcm_token'` ile idempotent olduğu için geçici hatada yeniden
  // denenmesi güvenli (retryTransient'in yazma uyarısının gerekçeli istisnası).
  // Saat sapması (PGRST303) tek denemede kaydı düşürüyordu: kullanıcı bildirim almamaya
  // başlıyor ve bunu fark etmek zor oluyordu — Sentry GUNUM-VAR-1K.
  const { error } = await withTransientRetry(
    () =>
      supabase.from('user_devices').upsert(
        {
          user_id: userId,
          fcm_token: token,
          platform,
          last_active: new Date().toISOString(),
        },
        { onConflict: 'fcm_token' }
      ) as unknown as Promise<{ data: null; error: { code?: string; message?: string } | null }>,
    { maxRetries: 3, delayMs: 1200 }
  )

  if (error) {
    captureError(error, { service: 'fcmService', action: 'saveDeviceToken', platform })
    return
  }

  try {
    const { error: cleanupError } = await supabase
      .from('user_devices')
      .delete()
      .eq('user_id', userId)
      .neq('fcm_token', token)
      .eq('platform', platform)

    if (cleanupError) {
      logWarn('⚠️ [FCM] Platform token temizliği sırasında hata:', cleanupError.message)
    } else {
    }
  } catch (cleanupErr) {
    captureError(cleanupErr, { service: 'fcmService', action: 'platformCleanup' })
  }
}

/**
 * Logout durumunda cihazın FCM token'ını siler.
 */
export async function removeDeviceToken(userId: string): Promise<void> {
  if (!userId) return

  try {
    const lastToken = localStorage.getItem(LAST_TOKEN_KEY)

    if (lastToken) {
      const { error } = await supabase
        .from('user_devices')
        .delete()
        .match({ user_id: userId, fcm_token: lastToken })

      if (error) {
        logWarn('⚠️ [FCM] DB token silme hatası:', error.message)
      } else {
      }

      localStorage.removeItem(LAST_TOKEN_KEY)
    }

    if (!Capacitor.isNativePlatform()) {
      const messaging = await getMessagingInstance()
      if (messaging) {
        const isUnregistered = await deleteToken(messaging)
        if (isUnregistered) {
        }
      }
    }
  } catch (error) {
    captureError(error, { service: 'fcmService', action: 'removeDeviceToken' })
  }
}

/**
 * Foreground'da gelen push mesajlarını dinle.
 * Native için Capacitor, Web için Firebase Messaging kullanılır.
 * @returns Unsubscribe fonksiyonu
 */
export function onForegroundMessage(
  callback: (payload: { title: string; body: string; data?: Record<string, string> }) => void
): () => void {
  let unsubscribe: (() => void) | null = null

  if (Capacitor.isNativePlatform()) {
    const p = FirebaseMessaging.addListener('notificationReceived', (event: any) => {
      callback({
        title: event.notification.title || 'Günüm Var!',
        body: event.notification.body || 'Yeni bildirim!',
        data: event.notification.data as any
      })
    })

    unsubscribe = () => {
      p.then(listener => listener.remove()).catch(() => {})
    }
  } else {
    getMessagingInstance().then(messaging => {
      if (messaging) {
        unsubscribe = onMessage(messaging, payload => {
          const title = payload.notification?.title ?? payload.data?.title ?? 'Günüm Var!'
          const body = payload.notification?.body ?? payload.data?.body ?? 'Yeni bildirim!'
          callback({ title, body, data: payload.data })
        })
      }
    })
  }

  return () => {
    unsubscribe?.()
  }
}

/**
 * Kullanıcı bildirime tıkladığında (background / killed) dinle.
 * Capacitor Native üzerinden çalışır. Web tarafında ServiceWorker URL'yi otomatik açar.
 * @returns Unsubscribe fonksiyonu
 */
export function onNotificationActionPerformed(
  callback: (data: Record<string, string>) => void
): () => void {
  if (Capacitor.isNativePlatform()) {
    const p = FirebaseMessaging.addListener('notificationActionPerformed', (event: any) => {
      if (event.notification?.data) {
        callback(event.notification.data)
      }
    })

    return () => {
      p.then(listener => listener.remove()).catch(() => {})
    }
  }
  return () => {}
}
